'use client';

import React from 'react';
import { UserX } from 'lucide-react';
import { cn } from '@sharpr/ui';
import type { QuestionablePlayer } from '@/hooks/useNbaBoard';
import { boardPlayerKey } from '@/lib/board/apply';
import { getPlayerImageUrl } from '../../utils/playerImages';
import { PlayerAvatar } from '../PlayerAvatar';
import { FilterShell } from './filters/FilterShell';
import { FILTER_CHIP_BASE } from './filters/FilterChip';

interface QuestionableFilterProps {
  players: QuestionablePlayer[];
  out: ReadonlySet<string>;              // boardPlayerKey(gameId, playerId) of the questionable players the viewer has out
  onToggle: (key: string) => void;
  onClearAll?: () => void;
  isCollapsed?: boolean;
  className?: string;
}

// The Conditionals filter (spec §4.4): one chip per questionable player, IN (the default) or OUT, starting IN. A game
// prices at the scenario its questionable players out make.
export function QuestionableFilter({ players, out, onToggle, onClearAll, isCollapsed = false, className }: QuestionableFilterProps) {
  if (players.length === 0) return null;
  // Grouped by game, then team.
  const sorted = [...players].sort((a, b) => a.gameId.localeCompare(b.gameId) || a.teamAbbr.localeCompare(b.teamAbbr) || a.name.localeCompare(b.name));
  const outCount = players.filter(p => out.has(boardPlayerKey(p.gameId, p.playerId))).length;

  return (
    <FilterShell
      icon={UserX}
      label="Conditionals"
      isCollapsed={isCollapsed}
      collapsedTooltip={outCount === 0 ? 'Everyone playing' : `${outCount} out`}
      collapsedBadge={outCount > 0 ? outCount : undefined}
      collapsedTone={outCount > 0 ? 'negative' : 'brand'}
      onClearAll={outCount > 0 && onClearAll ? onClearAll : undefined}
      summary={outCount > 0 ? <span className="text-xs font-medium text-red-600 dark:text-red-400">{outCount} out</span> : null}
      chipsGapClass="gap-1.5"
      className={className}
    >
      {sorted.map(p => {
        const key = boardPlayerKey(p.gameId, p.playerId);
        return <QuestionableChip key={key} player={p} isOut={out.has(key)} onToggle={() => onToggle(key)} />;
      })}
    </FilterShell>
  );
}

function QuestionableChip({ player, isOut, onToggle }: { player: QuestionablePlayer; isOut: boolean; onToggle: () => void }) {
  const parts = player.name.split(' ');
  const label = parts.length > 1 ? parts.slice(1).join(' ') : player.name;
  const imageUrl = getPlayerImageUrl(player.name, 'nba', player.teamAbbr, player.playerId);

  return (
    <button
      onClick={onToggle}
      aria-pressed={isOut}
      aria-label={`${player.name} (${player.teamAbbr}): ${isOut ? 'out' : 'playing'}`}
      className={cn(
        FILTER_CHIP_BASE,
        'gap-1.5',
        isOut
          ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 border-red-400 dark:border-red-600 shadow-sm hover:bg-red-200 dark:hover:bg-red-900/60'
          : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 border-emerald-400 dark:border-emerald-600 shadow-sm hover:bg-emerald-200 dark:hover:bg-emerald-900/60',
      )}
    >
      <PlayerAvatar name={player.name} imageUrl={imageUrl} size="xs" className={cn('border-0', isOut && 'opacity-50 grayscale')} />
      <span>{label}</span>
      <span className={cn('text-[10px] font-bold uppercase tracking-wide', isOut ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400')}>
        {isOut ? 'OUT' : 'IN'}
      </span>
    </button>
  );
}
